"use client";
import React from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

// Fonksiyon: Sayıyı uygun birime göre formatla (pozitif ve negatif sayılar için)
const formatNumber = (value) => {
  const absValue = Math.abs(value); // Mutlak değerini al
  let formattedValue;

  if (absValue >= 1_000_000_000) {
    formattedValue = (value / 1_000_000_000).toFixed(1) + " MR"; // Milyar
  } else if (absValue >= 1_000_000) {
    formattedValue = (value / 1_000_000).toFixed(1) + " M"; // Milyon
  } else if (absValue >= 1_000) {
    formattedValue = (value / 1_000).toFixed(1) + " B"; // Bin
  } else {
    formattedValue = value.toString(); // Binlikten küçükse direkt göster
  }

  return formattedValue;
};

const FinancialBarChart = ({ data, labels, title }) => {
  // Pozitif renk tonları (mavi ve yeşil - canlı renkler)
  const positiveColors = [
    "rgba(0, 255, 255, 1)", // Canlı Açık Mavi
    "rgba(0, 0, 255, 1)", // Canlı Mavi
    "rgba(0, 255, 0, 1)", // Canlı Açık Yeşil
    "rgba(0, 255, 125, 0.9)", // Canlı Koyu Yeşil
  ];

  // Negatif renk tonları (turuncu ve kırmızı - canlı renkler)
  const negativeColors = [
    "rgba(255, 179, 71, 1)", // Canlı Açık Turuncu
    "rgba(255, 87, 51, 1)", // Canlı Turuncu
    "rgba(255, 99, 132, 1)", // Canlı Açık Kırmızı
    "rgba(220, 53, 69, 1)", // Canlı Koyu Kırmızı
  ];

  // Verileri pozitif ve negatif olarak ayırıp sıralıyoruz (büyükten küçüğe)
  const positiveData = data.filter((value) => value >= 0).sort((a, b) => b - a);
  const negativeData = data.filter((value) => value < 0).sort((a, b) => b - a);

  // %15 fark kontrolü fonksiyonu
  const hasSignificantDifference = (current, prev) => {
    if (prev === 0 || !prev) return false;
    return Math.abs(((current - prev) / prev) * 100) > 15;
  };

  // Pozitif veriler için renkleri ayarlama
  const getPositiveBarColors = (data) => {
    return data.map((value, index) => {
      if (index > 0 && hasSignificantDifference(value, data[index - 1])) {
        // Eğer fark %15'ten büyükse daha koyu bir renk kullan
        return value >= data[Math.floor(data.length / 2)]
          ? positiveColors[3]
          : positiveColors[1];
      } else {
        // Fark yoksa daha açık renk kullan
        return value >= data[Math.floor(data.length / 2)]
          ? positiveColors[2]
          : positiveColors[0];
      }
    });
  };

  // Negatif veriler için renkleri ayarlama
  const getNegativeBarColors = (data) => {
    return data.map((value, index) => {
      if (index > 0 && hasSignificantDifference(value, data[index - 1])) {
        // Eğer fark %15'ten büyükse daha koyu bir renk kullan
        return value <= data[Math.floor(data.length / 2)]
          ? negativeColors[3]
          : negativeColors[1];
      } else {
        // Fark yoksa daha açık renk kullan
        return value <= data[Math.floor(data.length / 2)]
          ? negativeColors[2]
          : negativeColors[0];
      }
    });
  };

  // Tüm veriler için uygun renkleri ayarlama
  const getBarColors = (data) => {
    return data.map((value) => {
      if (value >= 0) {
        // Pozitif veriler için renk ayarlaması
        return getPositiveBarColors(positiveData)[positiveData.indexOf(value)];
      } else {
        // Negatif veriler için renk ayarlaması
        return getNegativeBarColors(negativeData)[negativeData.indexOf(value)];
      }
    });
  };
  const chartData = {
    labels: labels,
    datasets: [
      {
        label: title,
        data: data,
        backgroundColor: getBarColors(data), // Dinamik renkler
        borderColor: getBarColors(data), // Aynı renkleri sınır çizgisi olarak da kullan
        borderWidth: 0.5,
        borderRadius: 5, // Barların köşelerini yuvarlamak için
        barThickness: 25, // Sabit bar kalınlığı
        maxBarThickness: 40, // Maksimum bar kalınlığı
      },
    ],
  };

  const options = {
    scales: {
      x: {
        grid: {
          display: false, // X eksenindeki grid çizgilerini kaldır
        },
        ticks: {
          color: "#ffffff", // X ekseni yazı rengi
          font: {
            size: 12,
            weight: "bold",
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(255, 255, 255, 0.1)", // Y eksenindeki grid çizgilerini daha ince yap
        },
        ticks: {
          color: "#ffffff", // Y ekseni yazı rengi
          font: {
            size: 12,
            weight: "bold",
          },
          callback: function (value) {
            return formatNumber(value); // Y ekseni birimlerini dinamik olarak formatla
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false, // Grafiğin üstündeki başlığı gizle
      },
      title: {
        display: true,
        text: title,
        color: "#ffffff", // Başlık rengi
        font: {
          size: 14,
          weight: "bold",
        },
        padding: {
          top: 10,
          bottom: 30,
        },
      },
    },

    animation: false, // Animasyon tamamen kapalı
  };

  return (
    <div className="chart-wrapper">
      <Bar data={chartData} options={options} height={300} />{" "}
      {/* Canvas yüksekliği */}
    </div>
  );
};

export default FinancialBarChart;
